import React, { useMemo, useState } from "react";
import Navigation from "../components/site/Navigation";
import Footer from "../components/site/Footer";
import ParticleCursor from "../components/site/ParticleCursor";
import Reveal from "../components/site/Reveal";
import LivingSectionTitle from "../components/site/LivingSectionTitle";
import VisualAnchor from "../components/site/ui/VisualAnchor";
import JournalCard from "../components/journal/JournalCard";
import JournalSeo from "../components/journal/JournalSeo";
import { JOURNAL_POSTS, getFeaturedPost, FILTER_THEMES } from "../data/journalPosts";
import { trackEvent } from "../lib/track";

const JOURNAL_INTRO =
  "The Momentum Journal is a collection of reflections for the moments when effort is no longer the answer. These are not soft reminders. They are pattern interrupts for the parts of you that already know something needs to shift.";

const JournalIndexPage = () => {
  const featured = getFeaturedPost();
  const rest = useMemo(
    () => JOURNAL_POSTS.filter((p) => p.slug !== featured.slug),
    [featured.slug]
  );
  const [activeTheme, setActiveTheme] = useState(null);

  const visible = useMemo(() => {
    if (!activeTheme) return rest;
    return rest.filter((p) => p.themeTags.includes(activeTheme));
  }, [rest, activeTheme]);

  const onThemeClick = (t) => {
    const next = activeTheme === t ? null : t;
    setActiveTheme(next);
    trackEvent("journal_filter_changed", { theme: next || "all" });
  };

  return (
    <>
      <JournalSeo isIndex />
      <ParticleCursor />
      <Navigation />
      <main
        className="min-h-screen bg-themed noise-overlay pt-32 pb-28"
        data-testid="journal-index"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <header className="mb-12 max-w-3xl">
            <p className="text-[10px] tracking-[0.45em] uppercase text-gold/80 mb-5">
              The Momentum Journal
            </p>
            <LivingSectionTitle
              parts={[
                { text: "Reflections for the moment" },
                { text: "effort stops working.", italic: true },
              ]}
              align="left"
              variant="signal"
              className="text-4xl sm:text-5xl lg:text-6xl text-themed"
            />
            <Reveal delay={120}>
              <p className="mt-8 text-themed text-base md:text-lg leading-relaxed">
                {JOURNAL_INTRO}
              </p>
            </Reveal>
          </header>

          {/* OSIRIS — The Living Practice. Hidden Frame notebook anchor
              shown as a wide hero band above the featured reflection. */}
          <Reveal delay={140}>
            <div className="mb-20 relative" data-testid="journal-anchor-hero">
              <VisualAnchor
                src="/assets/brand/notebook.webp"
                alt="The Hidden Frame notebook. A living practice of attention."
                aspect="aspect-[1536/700] sm:aspect-[1536/640]"
                className="rounded-md border border-gold/15 bg-black/30"
                imgClassName="object-cover object-center"
              />
              <p className="mt-4 text-center text-[10px] tracking-[0.4em] uppercase text-gold/80">
                The Living Practice
              </p>
            </div>
          </Reveal>

          {/* Featured post */}
          <Reveal delay={150}>
            <div className="mb-20" data-testid="journal-featured">
              <p className="text-[10px] tracking-[0.4em] uppercase text-gold/85 mb-5">
                Featured Reflection
              </p>
              <JournalCard post={featured} featured />
            </div>
          </Reveal>

          {/* Theme filters */}
          <Reveal delay={200}>
            <div
              className="flex flex-wrap items-center gap-2 mb-10"
              data-testid="journal-filters"
            >
              <span className="text-[10px] tracking-[0.3em] uppercase text-themed-muted mr-2">
                Filter by theme
              </span>
              <button
                type="button"
                onClick={() => onThemeClick(null)}
                data-testid="journal-filter-all"
                className={`journal-filter-chip ${activeTheme === null ? "is-active" : ""}`}
              >
                All
              </button>
              {FILTER_THEMES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => onThemeClick(t)}
                  data-testid={`journal-filter-${t.toLowerCase()}`}
                  className={`journal-filter-chip ${activeTheme === t ? "is-active" : ""}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </Reveal>

          {/* Grid */}
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10"
            data-testid="journal-grid"
          >
            {visible.map((p, i) => (
              <Reveal key={p.slug} delay={i * 60}>
                <JournalCard post={p} />
              </Reveal>
            ))}
          </div>

          {visible.length === 0 && (
            <p className="text-themed-muted italic mt-8" data-testid="journal-empty">
              No reflections match that theme yet.
            </p>
          )}

          {/* Continued path */}
          <Reveal delay={120}>
            <div
              className="mt-28 grid grid-cols-1 md:grid-cols-3 gap-5"
              data-testid="journal-next-steps"
            >
              {[
                { label: "Find Your Current Signal", href: "/frequency-snapshot", note: "Two minutes. A reflection on your energy pattern." },
                { label: "Get the Book", href: "/book", note: "The framework behind everything written here." },
                { label: "Start the Personal Action Plan", href: "/?checkout=blueprint", note: "Turn reflection into external direction." },
              ].map((step) => (
                <a
                  key={step.href}
                  href={step.href}
                  className="journal-next-card"
                  data-testid={`journal-next-${step.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <p className="text-[10px] tracking-[0.35em] uppercase text-gold/75 mb-3">
                    Continue
                  </p>
                  <p className="font-serif text-themed text-xl md:text-2xl leading-snug">
                    {step.label}
                  </p>
                  <p className="text-white/85 text-[14.5px] mt-4 leading-relaxed">{step.note}</p>
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default JournalIndexPage;
